import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getImageKeys, importImage } from '../../api/images';

const IMPORTABLE = /\.(jpe?g|png|gif|webp|avif|svg|pdf|epub)$/i;

// Point at a folder (or select files); uploads only files not already in SnapVault,
// keeping their real names. `compact` renders a single inline button (for the Dashboard).
export default function FolderImport({ compact = false }) {
  const qc = useQueryClient();
  const folderRef = useRef(null);
  const filesRef  = useRef(null);
  const [st, setSt] = useState({ running: false, done: 0, total: 0, added: 0, skipped: 0, failed: 0, msg: '' });

  async function handleFiles(fileList) {
    const files = [...fileList].filter((f) => IMPORTABLE.test(f.name));
    if (!files.length) {
      setSt((s) => ({ ...s, running: false, msg: 'No images or PDFs in that selection.' }));
      return;
    }
    setSt({ running: true, done: 0, total: files.length, added: 0, skipped: 0, failed: 0, msg: 'Checking what’s already here…' });

    // Skip files already here (matched by name) so we upload only new ones.
    let existing = new Set();
    try { const { data } = await getImageKeys(); existing = new Set(data.keys || []); } catch { /* no skip list */ }
    const toUpload   = files.filter((f) => !existing.has(f.name));
    const preSkipped = files.length - toUpload.length;

    let done = preSkipped, added = 0, skipped = preSkipped, failed = 0;
    setSt((s) => ({ ...s, done, total: files.length, skipped, msg: '' }));

    let idx = 0;
    const worker = async () => {
      while (idx < toUpload.length) {
        const file = toUpload[idx++];
        const form = new FormData();
        form.append('image', file, file.name);
        try {
          const { data } = await importImage(form);
          if (data.duplicate) skipped++; else added++;
        } catch { failed++; }
        done++;
        setSt((s) => ({ ...s, done, added, skipped, failed }));
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, toUpload.length || 1) }, worker));

    qc.invalidateQueries({ queryKey: ['images'] });
    qc.invalidateQueries({ queryKey: ['drive', 'storage'] });
    setSt((s) => ({ ...s, running: false, msg: `Done — ${added} added, ${skipped} already there${failed ? `, ${failed} failed` : ''}.` }));
  }

  // Snapshot the FileList into a real array BEFORE resetting the input — clearing
  // value can empty the live FileList, which would drop every file.
  const onPick = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = '';
    if (files.length) handleFiles(files);
  };
  const pct = st.total ? Math.round((st.done / st.total) * 100) : 0;

  const inputs = (
    <>
      <input
        ref={(el) => { folderRef.current = el; if (el) { el.setAttribute('webkitdirectory', ''); el.setAttribute('directory', ''); } }}
        type="file" multiple className="hidden" onChange={onPick}
      />
      <input ref={filesRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={onPick} />
    </>
  );

  if (compact) {
    return (
      <div className="space-y-1.5 w-full">
        <div className="flex items-center gap-2 flex-wrap">
          <button className="btn-secondary text-sm" disabled={st.running} onClick={() => folderRef.current?.click()}>
            {st.running ? `Importing… ${pct}%` : '📁 Import folder'}
          </button>
          {!st.running && !st.msg && (
            <span className="text-xs text-gray-500">Bulk-import a folder — uploads only new files, keeps their names.</span>
          )}
          {!st.running && st.msg && <span className="text-xs text-green-400">{st.msg}</span>}
        </div>
        {st.running && (
          <div className="space-y-1 max-w-sm">
            <div className="h-1.5 rounded bg-gray-800 overflow-hidden">
              <div className="h-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
            </div>
            <p className="text-xs text-gray-400">
              {st.msg || `${st.done}/${st.total} · ${st.added} added · ${st.skipped} skipped${st.failed ? ` · ${st.failed} failed` : ''}`}
            </p>
          </div>
        )}
        {inputs}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-500">
        Point SnapVault at your phone’s Screenshots folder (or any folder). It uploads only files that aren’t already
        here, keeping their real names. Run it again anytime to add just the new ones.
      </p>

      <div className="flex flex-wrap gap-2">
        <button className="btn-primary text-sm" disabled={st.running} onClick={() => folderRef.current?.click()}>
          {st.running ? 'Importing…' : 'Choose folder…'}
        </button>
        <button className="btn-secondary text-sm" disabled={st.running} onClick={() => filesRef.current?.click()}>
          Choose files instead
        </button>
      </div>

      {inputs}

      {st.running && (
        <div className="space-y-1.5">
          <div className="h-2 rounded bg-gray-800 overflow-hidden">
            <div className="h-full bg-brand-500 transition-all" style={{ width: `${pct}%` }} />
          </div>
          <p className="text-xs text-gray-400">
            {st.msg || `${st.done}/${st.total} · ${st.added} added · ${st.skipped} skipped${st.failed ? ` · ${st.failed} failed` : ''}`}
          </p>
        </div>
      )}
      {!st.running && st.msg && <p className="text-sm text-green-400">{st.msg}</p>}

      <p className="text-[11px] text-gray-500">
        On Android, “Choose folder” opens the file manager — go into <span className="font-mono">DCIM/Screenshots</span> and select it.
        If your phone only lets you pick files, use “Choose files instead” and select all.
      </p>
    </div>
  );
}
